import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Users,
  Zap,
  TrendingUp,
  BookOpen,
  Brain,
  Award,
  Clock,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

export default function Class9Foundation() {
  // Framer motion variants
  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.12 * i, duration: 0.5 },
    }),
  };
  const openAdmissionEnquiry = () => {
    const phoneNumber = "919918519991";
    const message =
      "Hello SSS Achievers, I would like to enquire about admissions. Please share complete details. Thank you.";

    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  return (
    <>
    <Helmet>
      <title>Class 9 Foundation Course in Ghazipur | SSS Achievers Coaching</title>
      <meta name="description" content="SSS Achievers Class 9 Foundation course in Ghazipur – Advance your concepts in Maths, Science, English & Social Science. Expert faculty, small batches, weekly tests & free demo class. Best Class 9 coaching in Ghazipur, UP. Enroll now!" />
      <meta name="keywords" content="Class 9 coaching in Ghazipur, Class 9 Foundation course Ghazipur, best coaching for Class 9 in UP, SSS Achievers Class 9, Class 9 tuition Ghazipur, Maths Science coaching Ghazipur, coaching institute Ghazipur Uttar Pradesh, CBSE Class 9 coaching" />
      <link rel="canonical" href="https://www.sssachievers.com/courses/class-9-foundation" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Class 9 Foundation Course in Ghazipur | SSS Achievers" />
      <meta property="og:description" content="Build advanced concepts with SSS Achievers' Class 9 coaching in Ghazipur. Expert teachers, small batches, weekly tests, study material included. Free demo class available." />
      <meta property="og:url" content="https://www.sssachievers.com/courses/class-9-foundation" />
      <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content="SSS Achievers" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Class 9 Foundation Course in Ghazipur | SSS Achievers" />
      <meta name="twitter:description" content="Best Class 9 coaching in Ghazipur. Expert faculty, small batches, weekly tests. Free demo class at SSS Achievers." />
      <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Class 9 Foundation Course",
          "description": "Comprehensive Class 9 Foundation coaching in Ghazipur covering Maths, Science, English and Social Science with expert faculty and weekly assessments.",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "SSS Achievers",
            "url": "https://www.sssachievers.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "96/B, Badi Bagh",
              "addressLocality": "Ghazipur",
              "addressRegion": "Uttar Pradesh",
              "postalCode": "233001",
              "addressCountry": "IN"
            },
            "telephone": "+919918519991"
          },
          "url": "https://www.sssachievers.com/courses/class-9-foundation",
          "educationalLevel": "Class 9",
          "teaches": ["Mathematics", "Science", "English", "Social Science"],
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "onsite",
            "location": {
              "@type": "Place",
              "name": "SSS Achievers, Ghazipur",
              "address": "96/B, Badi Bagh, Ghazipur, UP 233001"
            }
          }
        }
      `}</script>
    </Helmet>
    <main
      className="min-h-screen bg-white text-slate-900 antialiased"
      style={{
        // define brand colors as CSS variables for convenience
        "--primary": "#0097b2",
        "--primary-50": "rgba(0,151,178,0.05)",
        "--primary-10": "rgba(0,151,178,0.10)",
        "--secondary": "#fcbf0a",
        "--secondary-10": "rgba(252,191,10,0.10)",
      }}
    >
      {/* Navigation */}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[var(--primary-50)] via-white to-[var(--secondary-10)] pt-10 pb-12 md:pt-20 md:pb-28">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[var(--secondary)]/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[var(--primary)]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              className="space-y-6"
            >
              <div className="inline-block mb-4 px-4 py-2 rounded-full bg-[var(--secondary)]/20">
                <span
                  className="text-lg font-semibold"
                  style={{ color: "var(--primary)" }}
                >
                  🎓 Class 9 Foundation
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
                A step toward academic excellence through clarity, practice, and
                consistency.
              </h1>

              <p className="text-md text-slate-700 max-w-2xl">
                Class 9 is the bridge between learning and performance — the
                year where understanding replaces memorization. At SSS
                Achievers, our Foundation (Class 9) program focuses on
                conceptual learning, analytical reasoning, and exam-oriented
                strategies.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  onClick={openAdmissionEnquiry}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[var(--primary)] text-white font-semibold shadow hover:shadow-lg transform-gpu hover:-translate-y-0.5 transition"
                >
                  Start Free Demo
                </a>

                <a
                  onClick={openAdmissionEnquiry}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg border-2 border-[var(--primary)] text-[var(--primary)] font-semibold hover:bg-[var(--primary)]/5 transition"
                >
                  Enroll Now
                </a>
              </div>

              <div className="mt-6 flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[var(--secondary)]" />
                  <span className="font-medium">Small Batch</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[var(--secondary)]" />
                  <span className="font-medium">10 Months</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="relative h-96 md:h-[420px] flex items-center justify-center"
            >
              <div className="relative">
                <div className="image-placeholder rounded-2xl h-96 md:h-full min-h-96 flex flex-col items-center justify-center gap-3 ">
                  <div className="text-center">
                    <img
                      src="../images/student_study.webp"
                      alt="student_study"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section id="learn-more" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
              className="text-3xl md:text-4xl font-bold"
            >
              Subjects Covered
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08, duration: 0.45 }}
              className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto"
            >
              Master core concepts across all subjects with interactive learning
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "📘",
                title: "Mathematics",
                desc: "Logical thinking & problem-solving",
              },
              {
                icon: "🧪",
                title: "Science",
                desc: "Experiments & concept clarity",
              },
              {
                icon: "📖",
                title: "English",
                desc: "Grammar & communication skills",
              },
              {
                icon: "🌍",
                title: "Social Science",
                desc: "History, civics & geography",
              },
            ].map((subject, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                custom={i}
                variants={fadeUp}
                className="p-6 border-2 border-gray-100 hover:border-[var(--primary)]/50 hover:shadow-lg transition-all duration-300 group rounded-xl"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {subject.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{subject.title}</h3>
                <p className="text-slate-600">{subject.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-20 bg-gradient-to-br from-[var(--primary-50)] to-[var(--secondary-10)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold"
            >
              Why Choose SSS Foundation Courses?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-3 text-lg text-slate-600"
            >
              Our Class 9 Foundation program prepares students to balance
              knowledge and performance. It helps them develop logical thinking
              and strong exam techniques while maintaining academic consistency.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Brain,
                title: "Deep Understanding",
                desc: "Strong grip on every topic for Class 10 readiness",
              },
              {
                icon: TrendingUp,
                title: "Weekly Tests & Feedback",
                desc: "Regular assessments to track steady improvement and identify gaps",
              },
              {
                icon: Users,
                title: "Doubt-Solving & Support",
                desc: "Transparent communication with parents and one-on-one guidance",
              },
              {
                icon: Zap,
                title: "AI Performance Tracking",
                desc: "Smart analytics for strength and weakness mapping",
              },
              {
                icon: Award,
                title: "Olympiad Preparation",
                desc: "Early exposure to Olympiad-level questions and reasoning",
              },
              {
                icon: BookOpen,
                title: "Comprehensive Materials",
                desc: "Printed and digital study materials designed by expert faculty",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.45 }}
                className="flex gap-4 p-6 bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-[var(--primary)]/10">
                    <feature.icon className="h-6 w-6 text-[var(--primary)]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-1">{feature.title}</h3>
                  <p className="text-slate-600">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Structure */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold"
            >
              Course Structure & Details
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { label: "Duration", value: "10 Months", icon: Clock },
              { label: "Batch Size", value: "Small Batch", icon: Users },
              { label: "Schedule", value: "Mon-Fri, 4-6 PM", icon: Clock },
              {
                label: "Weekly Tests",
                value: "Every Sunday, 9 AM",
                icon: TrendingUp,
              },
              { label: "Fee", value: "Price on request", icon: Award },
              { label: "Installments", value: "Available", icon: CheckCircle2 },
            ].map((item, i) => {
              const isFee = item.label === "Fee";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className={`p-6 border-2 border-gray-100 rounded-xl bg-gradient-to-br from-white to-[var(--primary-50)] ${
                    isFee ? "cursor-pointer hover:shadow-md" : ""
                  }`}
                  onClick={() => {
                    if (isFee) {
                      // navigate to contact page when "Fee" card is clicked
                      window.location.href = "/contact";
                    }
                  }}
                  role={isFee ? "button" : undefined}
                  tabIndex={isFee ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (isFee && (e.key === "Enter" || e.key === " ")) {
                      window.location.href = "/contact";
                    }
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-[var(--secondary)]/10">
                      <item.icon className="w-6 h-6 text-[var(--primary)]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 font-medium">
                        {item.label}
                      </p>
                      <p className="text-lg font-bold mt-1">{item.value}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-[var(--primary-10)] to-[var(--secondary-10)] rounded-3xl p-8 border-2 border-[var(--primary)]/10">
            <h3 className="text-2xl font-bold mb-6">Learning Outcomes</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Achieve strong conceptual clarity across core subjects",
                "Apply concepts to solve application-based and higher-order problems.",
                "Improve accuracy, speed and exam temperament through regular tests.",
                "Build a solid foundation for Class 10 board preparation.",
                "Track progress via AI analytics and targeted mentor interventions.",
              ].map((outcome, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 font-medium">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section className="py-20 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold"
            >
              Expert Faculty
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-3 text-lg text-slate-600"
            >
              Passionate educators with years of experience
            </motion.p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-gray-100">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-[var(--primary-10)] to-[var(--secondary-10)] rounded-full mx-auto mb-2 flex items-center justify-center">
                  <Users className="w-12 h-12 text-[var(--primary)]" />
                </div>
                <h3 className="text-lg font-bold">Experienced Educators</h3>
                <p className="text-slate-600">
                  Years of expertise in foundation and board-level teaching
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-[var(--primary-10)] to-[var(--secondary-10)] rounded-full mx-auto mb-2 flex items-center justify-center">
                  <Brain className="w-12 h-12 text-[var(--primary)]" />
                </div>
                <h3 className="text-lg font-bold">Personalized Support</h3>
                <p className="text-slate-600">
                  Focus on clarity, patience, and individual student needs
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-[var(--primary-10)] to-[var(--secondary-10)] rounded-full mx-auto mb-2 flex items-center justify-center">
                  <Award className="w-12 h-12 text-[var(--primary)]" />
                </div>
                <h3 className="text-lg font-bold">Proven Track Record</h3>
                <p className="text-slate-600">
                  Consistent results in exams and competitive platforms
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold"
            >
              Frequently Asked Questions
            </motion.h2>
          </div>

          <FAQAccordion faqs={[
            { q: "Will this program prepare me for Class 10?", a: "Absolutely — Class 9 strengthens foundations and reduces future load for Class 10." },
            { q: "How frequent are assessments?", a: "Weekly chapter tests + monthly full-length assessments with analytics." },
            { q: "Do students get one-to-one support?", a: "Yes — scheduled doubt clinics and mentor sessions for individual needs." },
            { q: "Can parents track performance?", a: "Parents receive monthly reports and detailed progress summaries." },
            { q: "Is there a demo class available?", a: "Yes — you can book a free demo class before joining to experience our teaching style." },
          ]} />
        </div>
      </section>
    </main>
    </>
  );
}
